/* eslint-disable no-underscore-dangle */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import VISUAL_MAPPER from 'modules/common/utils/visual-mapper';
import {
  addDays,
  addMonths,
  addWeeks,
  subDays,
  subWeeks,
  subMonths,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from 'date-fns';
import { VISUAL_FILTER_MAPPER } from './constants';

export const extractKeywordMeta = (id, visuals) => {
  const result = [];

  visuals.forEach((visual) => {
    Object.entries(visual.filters || {}).forEach(([filterName, filter]) => {
      if (filter.keyword) {
        result.push({
          scenarioId: id,
          visualId: visual?.id,
          filterName,
          keyword: filter.keyword,
          value: filter.value,
        });
      }
    });
  });
  return result;
};

export const getAvailableFilters = (visuals) => {
  const filters = [];
  const paceVisualDisplayName = [
    ...VISUAL_MAPPER.paceCancellationFilter,
    ...VISUAL_MAPPER.paceHotelSegmentFilterList,
    ...VISUAL_MAPPER.paceHotelSegmentTabularFilterList,
    ...VISUAL_MAPPER.paceHotelTabularFilterList,
    ...VISUAL_MAPPER.paceHotelVisualFilter,
    ...VISUAL_MAPPER.paceOccupiedRoomFilter,
    ...VISUAL_MAPPER.paceSegmentTabularFilterList,
    ...VISUAL_MAPPER.paceSegmentVisualFilter,
    ...VISUAL_MAPPER.paceTimeRevenueFilter,
    ...VISUAL_MAPPER.paceTimeSegmentFilterList,
    ...VISUAL_MAPPER.summaryVisualFilter,
  ];
  //
  visuals?.forEach((visual) => {
    const visualDisplayName = visual?.displayName;

    if (paceVisualDisplayName.includes(visualDisplayName)) {
      filters.push(...VISUAL_FILTER_MAPPER.PACE);
    }
    if (
      VISUAL_MAPPER.trendTimeFilterList.includes(visualDisplayName) ||
      VISUAL_MAPPER.trendTimeTabularFilterList.includes(visualDisplayName) ||
      VISUAL_MAPPER.trendSegmentFilterList.includes(visualDisplayName) ||
      VISUAL_MAPPER.trendSegmentTabularFilterList.includes(visualDisplayName) ||
      VISUAL_MAPPER.trendHotelNormalizedFilterList.includes(visualDisplayName) ||
      VISUAL_MAPPER.trendHotelSegmentTabularFilterList.includes(visualDisplayName) ||
      VISUAL_MAPPER.trendHotelSegmentFilterList.includes(visualDisplayName) ||
      VISUAL_MAPPER.trendHotelSegmentTabularFilterList.includes(visualDisplayName)
    ) {
      filters.push(...VISUAL_FILTER_MAPPER.TREND);
    }
    if (
      VISUAL_MAPPER.ratePlanFilterList.includes(visualDisplayName) ||
      VISUAL_MAPPER.consortiaFilterList.includes(visualDisplayName)
    ) {
      filters.push(...VISUAL_FILTER_MAPPER.DASHBOARD);
    }
  });
  return [...new Set(filters)];
};

export function updateScenarioFilter({ scenario, filterKey, keyword, value, allScenarios }) {
  // Find existing keyword in any visual
  const existingKeyword = scenario.visuals?.find((visual) => visual.filters?.[filterKey])
    ?.filters?.[filterKey]?.keyword;

  // Update scenario description & visuals
  const updatedScenario = {
    ...scenario,
    description: existingKeyword
      ? scenario.description.replace(new RegExp(existingKeyword, 'gi'), keyword)
      : scenario.description,
    visuals: scenario.visuals.map((visual) => {
      const filter = visual.filters?.[filterKey];

      if (filter && filter.value !== value) {
        return {
          ...visual,
          filters: {
            ...visual.filters,
            [filterKey]: {
              ...filter,
              keyword,
              value,
            },
          },
        };
      }

      return visual;
    }),
  };

  let updatedScenarios = allScenarios;
  if (allScenarios) {
    updatedScenarios = allScenarios.map((s) => (s.id === updatedScenario.id ? updatedScenario : s));
  }

  return { updatedScenario, updatedScenarios };
}
export const moveHotelSetToEnd = (filters) => {
  const hotelSetItem = filters.find((f) => f.name === 'hotelSet');
  const others = filters.filter((f) => f.name !== 'hotelSet');

  return hotelSetItem ? [...others, hotelSetItem] : filters;
};

/**
 * Calculates start and end dates from a range string
 * @param {string} rangeStr - Example: "Past 7 days", "Upcoming 3 months"
 * @returns {{ startDate: string, endDate: string }}
 */
export const getDateRange = (rangeStr) => {
  const now = new Date();
  // Matches: "Past 2 Days", "Next 3 Weeks", "This Month".
  const regex = /(Past|Upcoming|Next|This)\s*(\d+)?\s*(day|days|week|weeks|month|months)?/i;
  const match = rangeStr.match(regex);

  if (!match) throw new Error('Invalid range string');

  const [, direction, amountStr, unit] = match;
  const amount = amountStr ? parseInt(amountStr, 10) : 0;

  let startDate;
  let endDate;

  switch (direction.toLowerCase()) {
    case 'past':
      switch (unit?.toLowerCase()) {
        case 'day':
        case 'days':
          startDate = subDays(now, amount);
          endDate = now;
          break;
        case 'week':
        case 'weeks':
          startDate = subWeeks(now, amount);
          endDate = now;
          break;
        case 'month':
        case 'months':
          startDate = subMonths(now, amount);
          endDate = now;
          break;
        default:
          break;
      }
      break;

    case 'upcoming':
    case 'next':
      switch (unit?.toLowerCase()) {
        case 'day':
        case 'days':
          startDate = now;
          endDate = addDays(now, amount);
          break;
        case 'week':
        case 'weeks':
          startDate = now;
          endDate = addWeeks(now, amount);
          break;
        case 'month':
        case 'months':
          startDate = now;
          endDate = addMonths(now, amount);
          break;
        default:
          break;
      }
      break;

    case 'this':
      switch (unit?.toLowerCase()) {
        case 'day':
          startDate = startOfDay(now);
          endDate = endOfDay(now);
          break;
        case 'week':
          startDate = startOfWeek(now);
          endDate = endOfWeek(now);
          break;
        case 'month':
          startDate = startOfMonth(now);
          endDate = endOfMonth(now);
          break;
        default:
          break;
      }
      break;
    default:
      break;
  }

  return {
    startDate: startDate?.toISOString(),
    endDate: endDate?.toISOString(),
  };
};
